// A client's or consultant's scope of works, recognised by what the document
// says (CN4, Peter 2026-10-13: "file names shouldnt dictate anything. only the
// contents.").
//
// The scope of works Stage 1 lifts onto the project is the AUTHORITATIVE brief
// for the strategy, the Work Method and the quote. Before CN4 a file NAME could
// make a document that brief: "Scope.pdf", "Scope of Works.pdf", "... SOW
// (MMYY).rtf" and "scope document" were enough. So could a loose text rule
// (four numbered lines, the word "inclusions" and a trade word), which every
// one of the business's own quotes meets. This module reads the text alone. A
// file name, an extension and a folder are never inputs.
//
// A scope of works is:
//   - 'titled-scope'   a document titled as a scope of works at its top ("Scope
//                      of Works", "Remedial Scope of Works", "Required Scope",
//                      "Statement of Works"; not "Schedule of Works"), with the
//                      work it asks for set out as items;
//   - 'specification'  a specification of the work, titled so ("Engineering
//                      Specification", "Specification of Works", "Technical
//                      Specification") or issued as a tender package
//                      (conditions of tendering, a tender period), with work
//                      items or the contractor's obligations;
//   - 'tender-schedule' a schedule the tenderer prices ("Bill of Quantities",
//                      "Schedule of Rates", "Tender Schedule"), with work items.
//
// It is never (checked first, whatever the text also says):
//   - the estimator's own priced notes (detectEstimatorPricedNotes);
//   - a message (an email by its headers): the request travels in the message
//     and the scope in its attachment;
//   - a quote or a tender anyone sends, or its cover note, a builder's own
//     document under its letterhead, an invoice, or the office's inspection
//     request (detectRfqByContent's rejections);
//   - a blank template (placeholders such as "<Quote title here>", "SP####",
//     "DD/MM/YY");
//   - a report, unless its scope of works title comes first
//     (detectReportByContent);
//   - a drawing (detectPlanByContent).
//
// A request for a quote can be a scope of works too (a consultant's
// specification issued for tender). This module does not change whether a
// document is the RFQ; that stays detectRfqByContent's decision.
//
// Several rules were added after reading RJ Bird's documents on this PC
// (docs/contents-not-names/CN4-results-2026-10-14.md): the template rule, the
// title zone, the report precedence and the work-line count.
//
// Pure and synchronous. Reads the first SCOPE_SAMPLE_CHARS characters.

import { detectEstimatorPricedNotes } from './estimator-notes-detector';
import {
    countMatches,
    detectRfqByContent,
    normaliseDetectorText,
    TENDER_PACKAGE_RES,
    type RfqContentRejection,
} from './rfq-content-detector';
import {
    capitalisedTitle,
    detectPlanByContent,
    detectReportByContent,
    messageHeaders,
    type PageGeometryLike,
} from './report-plan-content-detector';

/** How much of a text is read, the same window the other content detectors read. */
export const SCOPE_SAMPLE_CHARS = 8000;

export type ScopeOfWorksKind = 'titled-scope' | 'specification' | 'tender-schedule';
export type ScopeOfWorksRejection =
    | 'estimator-notes'
    | 'message'
    | 'quote'
    | 'builder-document'
    | 'invoice'
    | 'inspection-request'
    | 'template'
    | 'report'
    | 'plan';

export interface ScopeOfWorksContentInput {
    /** The text read from the document. */
    text?: unknown;
    /** Page sizes in PDF units, first page first, when the document has pages. */
    pages?: PageGeometryLike[] | null;
}

export interface ScopeOfWorksContentResult {
    isScopeOfWorks: boolean;
    kind: ScopeOfWorksKind | null;
    /** The document's own scope or specification title, when it has one. */
    title: string | null;
    /** Lines that set out a piece of work ("Supply and install ...", "1.2 Remove ..."). */
    workLines: number;
    /** Statements of what the contractor is to do ("shall be", "is to be", "Contractor to"). */
    obligations: number;
    /** Why the text is not a scope of works, when a rejection decided it. */
    rejectedAs: ScopeOfWorksRejection | null;
    evidence: string[];
}

// ─── Reading the text ───────────────────────────────────────────────────────

// How far into the text the document's own title is looked for. A consultant's
// contact block (phone, web, email, street address) can sit above it.
const SCOPE_TITLE_CHARS = 400;

// Titles of a scope of works. "Schedule of Works" is left out: on RJ Bird's
// documents it heads a construction programme (when the work happens), not
// the work to be priced.
const SCOPE_TITLE_RES: RegExp[] = [
    /\b(?:(?:remedial|required|general|detailed|proposed|revised|updated|rectification|repair|building)\s+)?scope\s+of\s+(?:the\s+)?(?:(?:remedial|rectification|repair|building|proposed)\s+)?works?\b/i,
    /\b(?:required|remedial|rectification)\s+scope\b/i,
    /\bstatement\s+of\s+works?\b/i,
];
// Titles of a specification of the work.
const SPECIFICATION_TITLE_RES: RegExp[] = [
    /\bspecifications?\s+(?:of|for)\s+(?:the\s+)?(?:(?:remedial|rectification|repair|building|proposed)\s+)?works?\b/i,
    /\b(?:engineering|technical|remedial|rectification|repair|works?|building|project|tender)\s+specifications?\b/i,
];
// Titles of a schedule the tenderer prices.
const TENDER_SCHEDULE_TITLE_RES: RegExp[] = [
    /\bbill\s+of\s+quantities\b/i,
    /\bschedule\s+of\s+(?:rates|quantities)\b/i,
    /\btender\s+schedule\b/i,
];

// A line that sets out a piece of work, after any item number or bullet.
const WORK_VERBS = String.raw`(?:supply|install|remove|replace|repair|apply|provide|demolish|prepare|clean|seal|grind|construct|reinstate|excavate|paint|render|waterproof|flood\s+test|test|inspect|make\s+good|dispose|erect|dismantle|break\s+out|treat|prime|patch|fix|core|connect|lay|cut|strip|allow|carry\s+out|undertake|establish|conduct|maintain|coordinate|extend|rebuild|rectify|reseal|rebed|regrout|re-?\w+|fill|flush|sand|grout|cap|fit|mechanically|ensure|engage|obtain|protect|set\s+out|investigate|trim|adjust|tension|terminate|form|pour|drill|scarify|wash|stabilise|prop)`;
const WORK_LINE_RE = new RegExp(String.raw`^\s*(?:(?:\d{1,2}(?:\.\d{1,2}){0,3}|[a-z]|[ivx]{1,4})[.)]?\s+|[-–•·▪●◦*]\s*)?${WORK_VERBS}\b`, 'i');
// Bullets a PDF flattened onto one line ("Setup - Establish the work zone ...
// - Conduct site inductions ...").
const INLINE_BULLET_RE = /\s[-–•·▪●]\s+(?=[A-Z])/g;

// What the contractor is to do.
const OBLIGATION_RES: RegExp[] = [
    /\b(?:the\s+)?(?:contractor|builder|tenderer|subcontractor)\s+(?:shall|must|is\s+(?:to|required\s+to)|will\s+be\s+required\s+to|to\s+(?:supply|provide|allow|install|carry|ensure|remove|confirm|repair|replace|engage|submit|obtain))\b/gi,
    /\b(?:shall|is\s+to|are\s+to|must)\s+be\s+(?:supplied|installed|removed|replaced|repaired|carried\s+out|provided|applied|constructed|undertaken|completed|reinstated|made\s+good|sealed|cleaned|tested|inspected|painted|prepared|treated|disposed)\b/gi,
    /\bto\s+be\s+(?:supplied|installed|removed|replaced|repaired|carried\s+out|provided|applied|reinstated|made\s+good|sealed|cleaned|tested|treated|confirmed\s+by\s+the\s+(?:builder|contractor))\b/gi,
];

// A blank template: placeholders for the fields a person fills in.
const TEMPLATE_PLACEHOLDER_RES: RegExp[] = [
    /<[A-Za-z][^<>\n]{1,40}>/,
    /\b(?:SP|QR|WO|PO|Lot|Unit)\s?#{2,}/i,
    /\bDD\s*\/\s*MM\s*\/\s*YY(?:YY)?\b/i,
];

// Enough for a scope: three work lines, or three of the contractor's obligations.
const WORK_LINES_MIN = 3;
const OBLIGATIONS_MIN = 3;

function lines(text: string): string[] {
    return text
        // PDF text keeps separate items on one line with a run of spaces
        // between them; each item is read as its own line.
        .replace(/[ \t]{2,}/g, '\n')
        .replace(INLINE_BULLET_RE, '\n- ')
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);
}

function countObligations(text: string): number {
    return OBLIGATION_RES.reduce((total, pattern) => total + (text.match(pattern) || []).length, 0);
}

function notScope(rejectedAs: ScopeOfWorksRejection | null, evidence: string[], workLines = 0, obligations = 0): ScopeOfWorksContentResult {
    return { isScopeOfWorks: false, kind: null, title: null, workLines, obligations, rejectedAs, evidence };
}

// The rejections the request detector already reads, as this module names them.
const RFQ_REJECTIONS: Partial<Record<RfqContentRejection, ScopeOfWorksRejection>> = {
    quote: 'quote',
    'builder-document': 'builder-document',
    invoice: 'invoice',
    'inspection-request': 'inspection-request',
    reply: 'message',
};

// A line of prose: four or more lower-case words once any email or web
// address is set aside ("The quoted works at the above property have been
// completed", "To whom it may concern,"). A contact block, a date or an
// address is not prose.
const ADDRESS_TOKEN_RE = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+|\b(?:https?:\/\/|www\.)\S*|\b[\w-]+\.(?:com|net|org|gov|edu)(?:\.au)?\b/gi;
function proseLine(line: string): boolean {
    const words = line.replace(ADDRESS_TOKEN_RE, ' ').match(/\b[a-z]{2,}\b/g) || [];
    return words.length >= 4;
}

// The document's own title: a scope title that no line of prose precedes. A
// letter that later lists the work "completed" or "proposed" is a letter.
function titleIn(zone: string, patterns: RegExp[]): { text: string; index: number } | null {
    const found = capitalisedTitle(zone, patterns);
    if (!found) return null;
    const before = zone.slice(0, found.index).split('\n');
    return before.some(proseLine) ? null : found;
}

function readInput(input: unknown): { raw: string; pages: PageGeometryLike[] | null } {
    if (typeof input === 'string') return { raw: input, pages: null };
    if (input && typeof input === 'object') {
        const value = input as ScopeOfWorksContentInput;
        return { raw: typeof value.text === 'string' ? value.text : '', pages: Array.isArray(value.pages) ? value.pages : null };
    }
    return { raw: '', pages: null };
}

/**
 * Whether a document is a client's or consultant's scope of works, read from
 * its text (and its page sizes, when given) alone. Never reads a file name, an
 * extension or a folder.
 */
export function detectScopeOfWorksByContent(input: unknown): ScopeOfWorksContentResult {
    const { raw, pages } = readInput(input);
    const text = normaliseDetectorText(raw).slice(0, SCOPE_SAMPLE_CHARS);
    if (text.replace(/\s+/g, '').length < 150) return notScope(null, ['too little text to read']);

    const bodyLines = lines(text);
    const workLines = bodyLines.filter(line => WORK_LINE_RE.test(line)).length;
    const obligations = countObligations(text);

    // ── What a scope of works is not ───────────────────────────────────────
    const notes = detectEstimatorPricedNotes(raw);
    if (notes.isEstimatorNotes) {
        return notScope('estimator-notes', [`it reads as the estimator's own priced notes (${notes.evidence.join('; ')})`], workLines, obligations);
    }
    if (messageHeaders(text)) {
        return notScope('message', ['it is a message; the scope it names travels as its attachment'], workLines, obligations);
    }
    const request = detectRfqByContent(raw);
    const requestRejection = request.rejectedAs ? RFQ_REJECTIONS[request.rejectedAs] : undefined;
    if (requestRejection) {
        return notScope(requestRejection, request.evidence, workLines, obligations);
    }
    const placeholders = countMatches(text, TEMPLATE_PLACEHOLDER_RES);
    if (placeholders.length >= 2) {
        return notScope('template', [`it is a blank template (${placeholders.join(', ')})`], workLines, obligations);
    }

    // ── The document's own title ───────────────────────────────────────────
    // Each item on its own line, and an email subject is not a title.
    const zone = lines(text.slice(0, SCOPE_TITLE_CHARS).replace(/^[ \t>]*subject\s*:[^\n]*$/gim, '')).join('\n');
    const scopeTitle = titleIn(zone, SCOPE_TITLE_RES);
    const specificationTitle = titleIn(zone, SPECIFICATION_TITLE_RES);
    const scheduleTitle = titleIn(zone, TENDER_SCHEDULE_TITLE_RES);
    const titles = [scopeTitle, specificationTitle, scheduleTitle].filter((value): value is { text: string; index: number } => Boolean(value));
    const title = titles.sort((left, right) => left.index - right.index)[0] || null;
    const tenderMarks = countMatches(text, TENDER_PACKAGE_RES);

    const report = detectReportByContent(raw, request);
    if (report.isReport) {
        // A consultant's scope has report sections too (introduction,
        // background, prepared by). Its scope title, ahead of any report
        // title, decides.
        const reportTitleAt = report.title ? zone.search(new RegExp(report.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+'), 'i')) : -1;
        const scopeFirst = Boolean(title) && (reportTitleAt < 0 || title!.index <= reportTitleAt);
        if (!scopeFirst) {
            return notScope('report', report.evidence, workLines, obligations);
        }
    }
    const plan = detectPlanByContent({ text: raw, textLength: raw.length, pages });
    if (plan.isPlan) return notScope('plan', plan.evidence, workLines, obligations);

    // ── What a scope of works says ─────────────────────────────────────────
    const enoughWork = workLines >= WORK_LINES_MIN || obligations >= OBLIGATIONS_MIN;
    const counts = `${workLines} work line${workLines === 1 ? '' : 's'}, ${obligations} obligation${obligations === 1 ? '' : 's'}`;
    if (!enoughWork) {
        return notScope(null, [title ? `titled "${title.text}" but it sets out too little work (${counts})` : `no scope of works title and too little work (${counts})`], workLines, obligations);
    }
    if (title) {
        const kind: ScopeOfWorksKind = title === scopeTitle ? 'titled-scope' : title === specificationTitle ? 'specification' : 'tender-schedule';
        return {
            isScopeOfWorks: true,
            kind,
            title: title.text,
            workLines,
            obligations,
            rejectedAs: null,
            evidence: [`it is titled "${title.text}" and sets out the work (${counts})`],
        };
    }
    if (tenderMarks.length >= 2) {
        return {
            isScopeOfWorks: true,
            kind: 'specification',
            title: null,
            workLines,
            obligations,
            rejectedAs: null,
            evidence: [`it is a tender package (${tenderMarks.slice(0, 3).join(', ')}) that sets out the work (${counts})`],
        };
    }
    return notScope(null, [`no scope of works title (${counts})`], workLines, obligations);
}
